import type { SerializedExcalidrawElement } from "./domain.js";

type SceneElement = SerializedExcalidrawElement;

const coordinate = (point: SceneElement[string], axis: 0 | 1) => {
  const value = Array.isArray(point) ? point[axis] : undefined;
  return typeof value === "number" ? value : 0;
};

/** Indices of the points worth keeping, in drawing order: both ends — two
 * points is the least a stroke can have and still count as drawn
 * (`isNothingYet`, normalizationGuard.ts) — and the ones furthest left,
 * right, up and down, so the bounding box the frame rule places the element
 * by (`boundsOf`, framePolicy.ts) stays exactly what it was. */
const keptIndices = (points: readonly SceneElement[string][]) => {
  const extreme = (axis: 0 | 1, better: (a: number, b: number) => boolean) => {
    let found = 0;
    points.forEach((point, index) => {
      if (better(coordinate(point, axis), coordinate(points[found], axis))) {
        found = index;
      }
    });
    return found;
  };
  const less = (a: number, b: number) => a < b;
  const more = (a: number, b: number) => a > b;
  return [
    ...new Set([
      0,
      points.length - 1,
      extreme(0, less),
      extreme(0, more),
      extreme(1, less),
      extreme(1, more),
    ]),
  ].sort((a, b) => a - b);
};

/**
 * The deleted copy of an element as every editor sends and keeps it: a
 * deleted stroke cut down to at most six of its points, and the pressures
 * that go with them.
 *
 * A deleted element stays in the scene as a higher-version tombstone, so an
 * older copy re-sent by a reconnecting editor can never bring it back, and a
 * teacher's deletion stays refused to students (`isDeletedByTeacher`). None
 * of that reads its drawing: the strokes of a class that draws and erases
 * made deleted freedraw half of a 6 MB scene (live class, 10-26), which every
 * join downloaded and every verify round hashed. The editor that deleted an
 * element keeps its full copy on screen, so its own undo brings the whole
 * stroke back as a new, complete revision.
 *
 * Everything else stays — the id and revision, the owner and teacher tags,
 * the frame and bindings, the position and size — and the kept points span
 * the same box, so the frame rule judges a restore exactly as it would have
 * judged the whole stroke. Idempotent, and the same object when there is
 * nothing to cut.
 */
export const slimTombstone = (element: SceneElement): SceneElement => {
  const { points, pressures } = element;
  if (!element.isDeleted || !Array.isArray(points)) {
    return element;
  }
  const kept = keptIndices(points);
  if (kept.length === points.length) {
    return element;
  }
  const pick = (values: readonly SceneElement[string][]) =>
    kept.map((index) => values[index]);
  return {
    ...element,
    points: pick(points),
    ...(Array.isArray(pressures) && pressures.length === points.length
      ? { pressures: pick(pressures) }
      : {}),
  };
};
